// Evaluator for `${...}` loop placeholder expressions.
// Example: `${(i * 2) + j}` respects parentheses and string joins.
import type { LuisArrayVariables, LuisObjectVariables } from '../../types'
import { formatExpressionValue } from './placeholder-expression-format'
import { parseLoopExpressionValue } from './placeholder-expression-parser'
import { tokenizeLoopExpression } from './placeholder-expression-tokenize'
import type { LoopExpressionState } from './placeholder-expression-types'

// Evaluate one `${...}` expression against the current loop scope.
export function evaluateParsedLoopExpression(
  expression: string,
  scope: Record<string, string>,
  variables: Record<string, string> = {},
  objectVariables: LuisObjectVariables = {},
  arrays: LuisArrayVariables = {},
): string | null {
  const tokens = tokenizeLoopExpression(expression)

  // Stop when tokenization fails for the current expression.
  if (!tokens || tokens.length === 0) {
    // Return null for invalid or empty expression token streams.
    return null
  }

  const state: LoopExpressionState = { index: 0, tokens, scope, arrays, variables, objectVariables }
  const value = parseLoopExpressionValue(state)

  // Stop when parsing failed or left trailing tokens behind.
  if (!value || state.index !== state.tokens.length) {
    // Return null for incomplete expression parsing.
    return null
  }

  // Return the formatted expression evaluation result.
  return formatExpressionValue(value)
}
